import { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { api, type Client, type Booking, type Gallery, type Invoice, type ClientMessage } from "../lib/api";
import { navigate } from "../lib/route";
import { formatDate, formatPrice } from "../lib/format";
import { STATUS_COLORS, useStatusLabels } from "../lib/status";

interface Props {
  clientId: string;
}

// ── Invoice status colours ──────────────────────────────────────────────────
const INVOICE_STATUS_COLORS: Record<string, string> = {
  draft: "#94a3b8",
  sent:  "#3b82f6",
  paid:  "#10b981",
  void:  "#ef4444",
};

// ── New-client creation form ─────────────────────────────────────────────────
function NewClientForm() {
  const { t } = useTranslation();
  const [name, setName]   = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError]  = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim()) return;
    setSaving(true);
    setError(null);
    try {
      const created = await api.createClient({
        DisplayName: name.trim(),
        Email: email.trim(),
        Phone: phone.trim(),
        Notes: notes.trim(),
      });
      navigate(`/clients/${created.ID}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create");
      setSaving(false);
    }
  }

  return (
    <div>
      <button className="btn-secondary" onClick={() => navigate("/clients")} style={{ marginBottom: "1rem" }}>
        ← {t("clients.title")}
      </button>

      <h1 style={{ margin: "0 0 1.25rem" }}>{t("clients.new_form_title")}</h1>

      {error && <div className="alert alert-error" style={{ marginBottom: "1rem" }}>{error}</div>}

      <form onSubmit={handleSubmit} className="card">
        <div className="form-group">
          <label>{t("clients.name_label")} *</label>
          <input
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Familie Jansen"
            autoFocus
          />
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "1rem" }}>
          <div className="form-group">
            <label>{t("clients.email_label")}</label>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
          </div>
          <div className="form-group">
            <label>{t("clients.phone_label")}</label>
            <input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+31 6 12345678" />
          </div>
        </div>
        <div className="form-group">
          <label>{t("clients.notes_label")}</label>
          <textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} />
        </div>
        <div style={{ display: "flex", gap: "0.5rem" }}>
          <button type="submit" className="btn-primary" disabled={saving}>
            {saving ? "Aanmaken…" : "Aanmaken"}
          </button>
          <button type="button" className="btn-secondary" onClick={() => navigate("/clients")}>
            Annuleren
          </button>
        </div>
      </form>
    </div>
  );
}

// ── Routing wrapper ──────────────────────────────────────────────────────────
export function ClientDetailPage({ clientId }: Props) {
  if (clientId === "new") return <NewClientForm />;
  return <ExistingClientDetail clientId={clientId} />;
}

// ── Existing-client detail page ──────────────────────────────────────────────
function ExistingClientDetail({ clientId }: Props) {
  const { t } = useTranslation();
  const STATUS_LABELS = useStatusLabels();

  const [client, setClient]     = useState<Client | null>(null);
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [galleries, setGalleries] = useState<Gallery[]>([]);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState<string | null>(null);
  const [subdomainHost, setSubdomainHost] = useState<string>('');
  const [portfolioSlug, setPortfolioSlug] = useState<string>('');

  // Inline-edit state
  const [editing, setEditing]   = useState(false);
  const [editName, setEditName] = useState("");
  const [editEmail, setEditEmail] = useState("");
  const [editPhone, setEditPhone] = useState("");
  const [editNotes, setEditNotes] = useState("");
  const [saving, setSaving]     = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Client portal access link state
  const [tokenUrl, setTokenUrl]     = useState<string | null>(null);
  const [generating, setGenerating] = useState(false);

  // Messages
  const [messages, setMessages]         = useState<ClientMessage[]>([]);
  const [messageBody, setMessageBody]   = useState("");
  const [sendingMessage, setSendingMessage] = useState(false);

  function startEdit(c: Client) {
    setEditName(c.DisplayName);
    setEditEmail(c.Email);
    setEditPhone(c.Phone);
    setEditNotes(c.Notes);
    setSaveError(null);
    setEditing(true);
  }

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const [c, bks, gals, invs, msgs] = await Promise.all([
          api.getClient(clientId),
          api.listBookings(undefined, clientId),
          api.listGalleries(clientId),
          api.listInvoices(),
          api.listClientMessages(clientId).catch(() => [] as ClientMessage[]),
        ]);
        if (cancelled) return;
        setClient(c);
        setBookings(bks);
        setGalleries(gals);
        setInvoices(invs.filter((inv) => inv.ClientID === clientId));
        setMessages(msgs);
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : "Failed to load");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    void load();
    api.runtimeConfig().then((c) => setSubdomainHost(c.subdomain_host ?? '')).catch(() => {});
    api.getMyPortfolio().then((p) => setPortfolioSlug(p.Slug)).catch(() => {});
    return () => { cancelled = true; };
  }, [clientId]);

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    if (!client) return;
    setSaving(true);
    setSaveError(null);
    try {
      const updated = await api.updateClient(client.ID, {
        display_name: editName,
        email: editEmail,
        phone: editPhone,
        notes: editNotes,
      });
      setClient(updated);
      setEditing(false);
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : "Save failed");
    } finally {
      setSaving(false);
    }
  }

  const generateToken = useCallback(async () => {
    setGenerating(true);
    try {
      const result = await api.generateClientToken(clientId);
      setTokenUrl(result.token_url);
    } catch {
      // ignore — user can retry
    } finally {
      setGenerating(false);
    }
  }, [clientId]);

  const revokeToken = useCallback(async () => {
    try {
      await api.revokeClientTokens(clientId);
      setTokenUrl(null);
    } catch {
      // ignore
    }
  }, [clientId]);

  async function handleSendMessage() {
    const body = messageBody.trim();
    if (!body) return;
    setSendingMessage(true);
    try {
      const msg = await api.sendClientMessage(clientId, body);
      setMessages((prev) => [...prev, msg]);
      setMessageBody("");
    } catch {
      // silently ignore
    } finally {
      setSendingMessage(false);
    }
  }

  if (loading) return <p style={{ color: "var(--muted)" }}>Loading client…</p>;

  if (error || !client) {
    return (
      <div>
        <button className="btn-secondary" onClick={() => navigate("/clients")}>← {t("clients.title")}</button>
        <div className="alert alert-error" style={{ marginTop: "1rem" }}>{error ?? "Client not found"}</div>
      </div>
    );
  }

  // Filter invoices for this client (done in state already, but type-safe guard)
  const clientInvoices = invoices;

  const galleryShareUrl = (s: string) =>
    subdomainHost && portfolioSlug
      ? 'https://' + portfolioSlug + '.' + subdomainHost + '/share/g/' + s
      : window.location.origin + '/share/g/' + s;

  const galleryShareDisplay = (s: string) =>
    galleryShareUrl(s).replace(/^https?:\/\//, '');

  return (
    <div>
      <button className="btn-secondary" onClick={() => navigate("/clients")} style={{ marginBottom: "1rem" }}>
        ← {t("clients.title")}
      </button>

      {/* ── HEADER ── */}
      <div className="card" style={{ marginBottom: "1.25rem" }}>
        {editing ? (
          <>
            {saveError && <div className="alert alert-error" style={{ marginBottom: "0.75rem" }}>{saveError}</div>}
            <form onSubmit={handleSave}>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "1rem" }}>
                <div className="form-group">
                  <label>Naam *</label>
                  <input required value={editName} onChange={(e) => setEditName(e.target.value)} autoFocus />
                </div>
                <div className="form-group">
                  <label>E-mail</label>
                  <input type="email" value={editEmail} onChange={(e) => setEditEmail(e.target.value)} />
                </div>
                <div className="form-group">
                  <label>Telefoon</label>
                  <input value={editPhone} onChange={(e) => setEditPhone(e.target.value)} />
                </div>
              </div>
              <div className="form-group" style={{ marginTop: "0.5rem" }}>
                <label>Notities</label>
                <textarea rows={3} value={editNotes} onChange={(e) => setEditNotes(e.target.value)} />
              </div>
              <div style={{ display: "flex", gap: "0.5rem", marginTop: "0.75rem" }}>
                <button type="submit" className="btn-primary" disabled={saving}>
                  {saving ? "Opslaan…" : "Opslaan"}
                </button>
                <button type="button" className="btn-secondary" onClick={() => setEditing(false)}>
                  Annuleren
                </button>
              </div>
            </form>
          </>
        ) : (
          <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "1rem", flexWrap: "wrap" }}>
            <div>
              <h1 style={{ margin: "0 0 0.25rem" }}>{client.DisplayName}</h1>
              {client.Email && (
                <p style={{ margin: "0 0 0.125rem", color: "var(--muted)", fontSize: "0.9rem" }}>
                  <a href={`mailto:${client.Email}`}>{client.Email}</a>
                </p>
              )}
              {client.Phone && (
                <p style={{ margin: "0 0 0.25rem", color: "var(--muted)", fontSize: "0.9rem" }}>
                  <a href={`tel:${client.Phone}`}>{client.Phone}</a>
                </p>
              )}
              {client.Notes && (
                <p style={{ margin: "0.5rem 0 0", color: "var(--muted)", fontSize: "0.875rem", whiteSpace: "pre-wrap" }}>
                  {client.Notes}
                </p>
              )}
            </div>
            <button className="btn-secondary" onClick={() => startEdit(client)} style={{ flexShrink: 0 }}>
              Bewerken
            </button>
          </div>
        )}
      </div>

      {/* ── CLIENT PORTAL ── */}
      <div className="card" style={{ marginBottom: "1.25rem" }}>
        <div style={{ display: "flex", alignItems: "center", gap: ".75rem", marginBottom: ".5rem" }}>
          <h3 style={{ margin: 0 }}>{t("clients.portal_title")}</h3>
          {client.IAMSubject ? (
            <span style={{ fontSize: ".75rem", fontWeight: 600, padding: ".15rem .5rem", borderRadius: 999, background: "color-mix(in srgb, #10b981 15%, transparent)", color: "#10b981", border: "1px solid color-mix(in srgb, #10b981 35%, transparent)" }}>
              Account linked
            </span>
          ) : (
            <span style={{ fontSize: ".75rem", fontWeight: 600, padding: ".15rem .5rem", borderRadius: 999, background: "color-mix(in srgb, #94a3b8 15%, transparent)", color: "#94a3b8", border: "1px solid color-mix(in srgb, #94a3b8 35%, transparent)" }}>
              No account
            </span>
          )}
        </div>
        <p style={{ color: "var(--muted)", fontSize: ".875rem" }}>
          {t("clients.portal_desc")}
        </p>
        {tokenUrl ? (
          <div style={{ display: "flex", gap: ".5rem", alignItems: "center", flexWrap: "wrap" }}>
            <input
              readOnly
              value={tokenUrl}
              style={{ flex: 1, fontSize: ".8rem" }}
              onClick={(e) => (e.target as HTMLInputElement).select()}
            />
            <button className="btn-ghost" onClick={() => void navigator.clipboard.writeText(tokenUrl)}>Kopiëren</button>
            <button className="btn-ghost" style={{ color: "#ef4444" }} onClick={() => void revokeToken()}>{t("clients.portal_revoke")}</button>
          </div>
        ) : (
          <button className="btn-ghost" onClick={() => void generateToken()} disabled={generating}>
            {generating ? t("clients.portal_generating") : t("clients.portal_generate")}
          </button>
        )}
        {tokenUrl && <small style={{ color: "var(--muted)" }}>{t("clients.portal_valid")}</small>}
      </div>

      {/* ── BOEKINGEN ── */}
      <div className="card" style={{ marginBottom: "1.25rem" }}>
        <h3 style={{ marginTop: 0 }}>Boekingen ({bookings.length})</h3>
        {bookings.length === 0 ? (
          <p style={{ margin: 0, color: "var(--muted)" }}>{t("clients.no_bookings")}</p>
        ) : (
          <div style={{ display: "grid", gap: "0.5rem" }}>
            {bookings.map((b) => (
              <div
                key={b.ID}
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: "0.75rem",
                  flexWrap: "wrap",
                  padding: "0.6rem 0.25rem",
                  borderBottom: "1px solid var(--border, #2c3640)",
                  cursor: "pointer",
                }}
                onClick={() => navigate("/bookings/" + b.ID)}
              >
                <span
                  style={{
                    background: STATUS_COLORS[b.Status],
                    color: "white",
                    padding: "0.125rem 0.5rem",
                    borderRadius: 999,
                    fontSize: "0.75rem",
                    fontWeight: 600,
                    flexShrink: 0,
                  }}
                >
                  {STATUS_LABELS[b.Status]}
                </span>
                <div style={{ flex: 1, minWidth: 120 }}>
                  <strong>{b.Title}</strong>
                  <div style={{ color: "var(--muted)", fontSize: "0.875rem", display: "flex", gap: "0.75rem", flexWrap: "wrap", marginTop: "0.125rem" }}>
                    {b.ScheduledAt && <span>{formatDate(b.ScheduledAt)}</span>}
                    {b.PriceCents > 0 && <span>{formatPrice(b.PriceCents, b.Currency)}</span>}
                    {b.PackageName && <span>{b.PackageName}</span>}
                  </div>
                </div>
                <span style={{ color: "var(--muted)", fontSize: "0.8rem", alignSelf: "center" }}>Bekijken →</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── GALERIJEN ── */}
      <div className="card" style={{ marginBottom: "1.25rem" }}>
        <h3 style={{ marginTop: 0 }}>Galerijen ({galleries.length})</h3>
        {galleries.length === 0 ? (
          <p style={{ margin: 0, color: "var(--muted)" }}>{t("clients.no_galleries")}</p>
        ) : (
          <div style={{ display: "grid", gap: "0.5rem", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))" }}>
            {galleries.map((g) => (
              <div
                key={g.ID}
                className="card"
                style={{ cursor: "pointer" }}
                onClick={() => navigate(`/galleries/${g.Slug}`)}
              >
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                  <strong>{g.Title}</strong>
                  <span style={{ color: "var(--muted)", fontSize: "0.85rem" }}>
                    {g.AssetCount} foto{g.AssetCount === 1 ? "" : "'s"}
                  </span>
                </div>
                <small style={{ display: "block", color: "var(--muted)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  <a
                    href={galleryShareUrl(g.Slug)}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: "var(--muted)", textDecoration: "none" }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    {galleryShareDisplay(g.Slug)}
                  </a>
                </small>
                <div style={{ marginTop: "0.5rem", display: "flex", gap: "0.4rem", flexWrap: "wrap" }}>
                  {g.IsPublic && <span className="badge badge-info">Publiek</span>}
                  {g.AllowDownloads && <span className="badge badge-info">Downloads</span>}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── FACTUREN ── */}
      <div className="card" style={{ marginBottom: "1.25rem" }}>
        <h3 style={{ marginTop: 0 }}>Facturen ({clientInvoices.length})</h3>
        {clientInvoices.length === 0 ? (
          <p style={{ margin: 0, color: "var(--muted)" }}>Geen facturen gekoppeld aan deze klant.</p>
        ) : (
          <div style={{ display: "grid", gap: "0" }}>
            {clientInvoices.map((inv) => (
              <div
                key={inv.ID}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "0.75rem",
                  flexWrap: "wrap",
                  padding: "0.6rem 0.25rem",
                  borderBottom: "1px solid var(--border, #2c3640)",
                  cursor: "pointer",
                }}
                onClick={() => navigate("/invoices")}
              >
                <span
                  style={{
                    background: INVOICE_STATUS_COLORS[inv.Status] ?? "#94a3b8",
                    color: "white",
                    padding: "0.125rem 0.5rem",
                    borderRadius: 999,
                    fontSize: "0.75rem",
                    fontWeight: 600,
                    flexShrink: 0,
                    textTransform: "capitalize",
                  }}
                >
                  {inv.Status}
                </span>
                <div style={{ flex: 1, minWidth: 100 }}>
                  <strong>#{inv.Number}</strong>
                </div>
                <span style={{ color: "var(--muted)", fontSize: "0.875rem" }}>
                  {inv.CreatedAt ? formatDate(inv.CreatedAt) : "—"}
                </span>
                <span style={{ fontWeight: 600, minWidth: 80, textAlign: "right" }}>
                  {formatPrice(inv.TotalCents, inv.Currency)}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── MESSAGES ── */}
      <div className="card">
        <h3 style={{ marginTop: 0 }}>Messages</h3>
        {messages.length === 0 ? (
          <p style={{ margin: "0 0 0.75rem", color: "var(--muted)", fontSize: "0.875rem" }}>No messages yet.</p>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem", marginBottom: "1rem" }}>
            {messages.map((m) => {
              const isPhotographer = m.SenderKind === "photographer";
              return (
                <div
                  key={m.ID}
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: isPhotographer ? "flex-end" : "flex-start",
                  }}
                >
                  <div style={{
                    maxWidth: "75%",
                    background: isPhotographer ? "var(--primary-subtle, rgba(99,102,241,.12))" : "var(--surface, #1e2730)",
                    border: "1px solid var(--border, #2c3640)",
                    borderRadius: ".6rem",
                    padding: ".5rem .75rem",
                    fontSize: "0.875rem",
                  }}>
                    <div style={{ fontSize: ".72rem", color: "var(--muted)", marginBottom: ".25rem" }}>
                      {m.SenderName} · {new Date(m.CreatedAt).toLocaleString()}
                    </div>
                    <div style={{ whiteSpace: "pre-wrap" }}>{m.Body}</div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
        <div style={{ display: "flex", gap: "0.5rem", alignItems: "flex-start" }}>
          <textarea
            placeholder="Type a message…"
            value={messageBody}
            onChange={(e) => setMessageBody(e.target.value)}
            rows={2}
            style={{ flex: 1, fontSize: "0.875rem", resize: "vertical" }}
            onKeyDown={(e) => {
              if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
                e.preventDefault();
                void handleSendMessage();
              }
            }}
          />
          <button
            className="btn-primary"
            style={{ fontSize: "0.875rem", padding: "0.4rem 0.9rem", alignSelf: "flex-end" }}
            disabled={sendingMessage || !messageBody.trim()}
            onClick={() => void handleSendMessage()}
          >
            {sendingMessage ? "…" : "Send"}
          </button>
        </div>
      </div>
    </div>
  );
}
